// Kho thí nghiệm Lab IoT (lab.vatli365.vn) tra cứu theo BÀI.
// id mã hoá: exp-<lop>-<chuong>-<bai>-<n> → parse ra lop/chuong/bai (khớp PPCT).
// Dùng để đề xuất "Làm thí nghiệm" (link sang Lab) cho từng bài — tích hợp năng lực STEM/AI/nhóm.

export interface LabExperiment {
  id: string
  title: string
  url: string        // path trên lab.vatli365.vn, vd /harmonic-motion
  lop: number
  chuong: number
  bai: number
}

export const LAB_BASE_URL = 'https://lab.vatli365.vn'

// Danh sách thí nghiệm (đồng bộ thủ công từ lab.vatli365.vn/src/data/experiments.json).
const LAB_RAW: { id: string; title: string; url: string }[] = [
  { id: 'exp-10-1-3-1', title: 'Đo tốc độ tức thời với ESP32', url: '/instant-speed' },
  { id: 'exp-10-1-4-1', title: 'Vẽ đồ thị độ dịch chuyển – thời gian', url: '/displacement-graph' },
  { id: 'exp-10-1-4-2', title: 'Đồ thị x(t) trên mặt phẳng nghiêng (BMI160 IoT)', url: '/displacement-time-bmi160' },
  { id: 'exp-10-1-5-1', title: 'Nghiên cứu chuyển động biến đổi (BMI160)', url: '/variable-motion' },
  { id: 'exp-10-1-5-2', title: 'Nghiên cứu chuyển động biến đổi (Laser TOF)', url: '/variable-motion-tof' },
  { id: 'exp-10-1-6-1', title: 'Đo gia tốc rơi tự do với ESP32', url: '/free-fall' },
  { id: 'exp-10-1-7-1', title: 'Nghiên cứu chuyển động ném (BMI160)', url: '/projectile' },
  { id: 'exp-10-2-8-1', title: 'Định luật I Newton (Quán tính)', url: '/newton-law-1' },
  { id: 'exp-10-2-8-2', title: 'Định luật II Newton với BMI160', url: '/newton-law-2' },
  { id: 'exp-10-2-8-3', title: 'Định luật III Newton (Tương tác)', url: '/newton-law-3' },
  { id: 'exp-10-2-9-1', title: 'Đo hệ số ma sát trượt', url: '/friction' },
  { id: 'exp-10-2-9-2', title: 'Đo hệ số ma sát trượt (BMI160 IoT)', url: '/friction-bmi160' },
  { id: 'exp-10-3-14-1', title: 'Bảo toàn cơ năng', url: '/energy-conservation' },
  { id: 'exp-10-3-17-1', title: 'Va chạm đàn hồi và Bảo toàn động lượng', url: '/momentum-collision' },
  { id: 'exp-11-1-1-1', title: 'Khảo sát Phương trình Dao động điều hòa (ADXL345)', url: '/harmonic-motion' },
  { id: 'exp-11-1-1-2', title: 'Đo đồ thị dao động — A, T, f, Pha (BMI160)', url: '/describe-oscillation-bmi160' },
  { id: 'exp-11-1-2-1', title: 'Khảo sát x(t), v(t), a(t) — Phương trình dao động (BMI160 · AI)', url: '/harmonic-motion-bmi160' },
  { id: 'exp-11-1-3-1', title: 'Năng lượng trong DĐĐH — Đồ thị W(t) và W(x) (BMI160 · AI)', url: '/energy-shm-bmi160' },
  { id: 'exp-11-1-4-1', title: 'Dao động tắt dần — Đường bao biên độ (BMI160 · AI)', url: '/damped-oscillation-bmi160' },
  { id: 'exp-11-1-4-2', title: 'Khảo sát Hiện tượng cộng hưởng', url: '/resonance-bmi160' },
  { id: 'exp-11-3-14-1', title: 'Quá trình nạp và phóng điện của tụ điện', url: '/capacitor' },
  { id: 'exp-11-4-16-1', title: 'Đặc tuyến V–A của điện trở', url: '/resistor-va' },
  { id: 'exp-11-4-16-2', title: 'Đặc tuyến V–A của đèn sợi đốt', url: '/lamp-va' },
  { id: 'exp-11-4-16-3', title: 'Đặc tuyến R(T) của điện trở nhiệt (NTC)', url: '/ntc-va' },
  { id: 'exp-11-4-16-4', title: 'Đặc tuyến R(T) của điện trở nhiệt (PTC)', url: '/ptc-va' },
  { id: 'exp-11-4-17-1', title: 'Đo suất điện động và điện trở trong của nguồn điện', url: '/battery-measure' },
  { id: 'exp-12-1-4-1', title: 'Xác định Nhiệt dung riêng của nước', url: '/specific-heat' },
  { id: 'exp-12-1-5-1', title: 'Xác định Nhiệt nóng chảy riêng của nước đá', url: '/melting-heat' },
  { id: 'exp-12-1-5-2', title: 'Nhiệt nóng chảy riêng (Digital — Loadcell)', url: '/melting-heat-digital' },
  { id: 'exp-12-1-6-1', title: 'Xác định Nhiệt hóa hơi riêng của nước', url: '/vaporization-heat' },
  { id: 'exp-12-3-14-1', title: 'Hiện tượng cảm ứng điện từ (ADS1115)', url: '/induction' },
]

const LAB_REGISTRY: LabExperiment[] = LAB_RAW.map((e) => {
  const m = e.id.match(/^exp-(\d+)-(\d+)-(\d+)-/)
  return {
    ...e,
    lop: m ? Number(m[1]) : 0,
    chuong: m ? Number(m[2]) : 0,
    bai: m ? Number(m[3]) : 0,
  }
})

export const LabService = {
  getByLesson(lop: number, chuong: number, bai: number): LabExperiment[] {
    return LAB_REGISTRY.filter((e) => e.lop === lop && e.chuong === chuong && e.bai === bai)
  },
  getAll(): LabExperiment[] {
    return [...LAB_REGISTRY].sort((a, b) => {
      if (a.lop !== b.lop) return a.lop - b.lop
      if (a.chuong !== b.chuong) return a.chuong - b.chuong
      return a.bai - b.bai
    })
  },
  fullUrl(exp: LabExperiment): string {
    return `${LAB_BASE_URL}${exp.url}`
  },
}

export default LabService
